import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, Clock, Ban, Scale, Mail } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { AutoTranslate } from '@/components/AutoTranslate';
import { MiniLanguageToggle } from '@/components/MiniLanguageToggle';

const TermsOfServicePage = () => {
  const lastUpdated = "March 3, 2026";
  
  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/terms"
        title="Terms of Service — ClearCommunicationApp"
        description="The terms governing your use of ClearCommunicationApp including subscriptions, content licensing, and acceptable use."
      />
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to App</span>
            </Link>
            <MiniLanguageToggle className="ml-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <AutoTranslate>
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>

          {/* Introduction */}
          <section className="prose dark:prose-invert max-w-none">
            <p className="text-lg leading-relaxed">
              Welcome to ClearCommunicationApp. By accessing or using our application, you agree to be bound 
              by these Terms of Service. Please read them carefully before using our services.
            </p>
          </section>

          {/* Sections */}
          <div className="space-y-8">
            {/* Acceptance of Terms */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">1. Acceptance of Terms</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>By creating an account or using ClearCommunicationApp, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>These Terms of Service</li>
                      <li>Our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link></li>
                      <li>All applicable laws and regulations</li>
                    </ul>
                    <p>If you do not agree with any part of these terms, you may not use our service.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Description of Service */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">2. Description of Service</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>ClearCommunicationApp is an educational platform designed to help users understand implied meanings in communication. Our service includes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>A library of common phrases with explanations</li>
                      <li>Audio pronunciations and regional variations</li>
                      <li>Gamification features including streaks, XP, and challenges</li>
                      <li>Social features including friends and leaderboards</li>
                      <li>Premium subscription options</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* User Accounts */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Scale className="h-6 w-6 text-green-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">3. User Accounts</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong className="text-foreground">Account Creation:</strong> You must provide accurate information when creating an account. You are responsible for maintaining the security of your account credentials.</p>
                    <p><strong className="text-foreground">Age Requirement:</strong> You must be at least 13 years old to use this service. Users under 18 should have parental consent.</p>
                    <p><strong className="text-foreground">Account Security:</strong> You are responsible for all activity that occurs under your account. Notify us immediately of any unauthorized use.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Subscription and Payments */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">4. Subscriptions and Payments</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong className="text-foreground">Free Tier:</strong> Basic access to the phrase library with daily limits.</p>
                    <p><strong className="text-foreground">VIP Subscription:</strong> Unlimited access to all features for a monthly or annual fee.</p>
                    <p><strong className="text-foreground">Billing:</strong> Subscriptions are processed through Stripe. You authorize us to charge your payment method on a recurring basis.</p>
                    <p><strong className="text-foreground">Cancellation:</strong> You may cancel your subscription at any time. Access continues until the end of your billing period.</p>
                    <p><strong className="text-foreground">Refunds:</strong> Refunds are handled on a case-by-case basis. Contact support for refund requests.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Prohibited Conduct */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <Ban className="h-6 w-6 text-red-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">5. Prohibited Conduct</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>You agree NOT to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Use the service for any illegal purpose</li>
                      <li>Harass, abuse, or harm other users</li>
                      <li>Attempt to access other users' accounts</li>
                      <li>Use automated systems to access the service</li>
                      <li>Copy, distribute, or modify our content without permission</li>
                      <li>Interfere with the proper functioning of the service</li>
                      <li>Create multiple accounts to abuse free tier limits</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <FileText className="h-6 w-6 text-purple-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">6. Intellectual Property</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>All content in ClearCommunicationApp, including but not limited to text, graphics, logos, audio, and software, is the property of ClearCommunicationApp or its licensors.</p>
                    <p>You may not reproduce, distribute, modify, or create derivative works from our content without explicit permission.</p>
                    <p>The phrase definitions, audio recordings, and educational content are protected by copyright.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Disclaimers */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <AlertTriangle className="h-6 w-6 text-orange-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">7. Disclaimers</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong className="text-foreground">Educational Purpose:</strong> Our content is for educational purposes only and should not be considered professional advice.</p>
                    <p><strong className="text-foreground">No Warranty:</strong> The service is provided "as is" without warranties of any kind, either express or implied.</p>
                    <p><strong className="text-foreground">Availability:</strong> We do not guarantee that the service will be available at all times or free from errors.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-gray-500/10">
                  <Scale className="h-6 w-6 text-gray-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">8. Limitation of Liability</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>To the maximum extent permitted by law, ClearCommunicationApp shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.</p>
                    <p>Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Termination */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <Ban className="h-6 w-6 text-red-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">9. Termination</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>We may terminate or suspend your account at any time for violation of these terms.</p>
                    <p>You may delete your account at any time through the Settings page or by contacting support.</p>
                    <p>Upon termination, your right to use the service ceases immediately.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Changes to Terms */}
            <section className="bg-muted/50 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-3">10. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms of Service at any time. We will notify users of significant changes 
                by posting a notice on the app or sending an email. Continued use of the service after changes 
                constitutes acceptance of the updated terms.
              </p>
            </section>

            {/* EU Geo-blocking Regulation 2018/302 compliance */}
            <section id="eu-geoblocking" className="bg-card rounded-xl p-6 border border-border scroll-mt-24">
              <h2 className="text-xl font-semibold mb-3">
                11. EU Geo-blocking Regulation (EU) 2018/302
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  ClearCommunicationApp complies with Regulation (EU) 2018/302 of
                  the European Parliament and of the Council on addressing
                  unjustified geo-blocking and other forms of discrimination based
                  on customers' nationality, place of residence, or place of
                  establishment within the internal market.
                </p>
                <p>In practice this means:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <strong className="text-foreground">No country-based access blocks.</strong>{' '}
                    Users from any EU/EEA Member State can access our website,
                    create an account, and use the service on the same terms as
                    customers in any other Member State.
                  </li>
                  <li>
                    <strong className="text-foreground">No discriminatory pricing.</strong>{' '}
                    Subscription prices, free-trial terms, and promotional offers
                    are identical regardless of which Member State the customer
                    accesses the site from.
                  </li>
                  <li>
                    <strong className="text-foreground">Equal payment methods.</strong>{' '}
                    We accept the same payment instruments (including SEPA
                    cards and major EU-supported wallets) from customers across
                    the EU/EEA. Payments are processed in EUR or the customer's
                    local supported currency without surcharges based on
                    nationality or residence.
                  </li>
                  <li>
                    <strong className="text-foreground">No forced re-routing.</strong>{' '}
                    We do not automatically redirect EU/EEA visitors to a
                    different version of the site without their explicit consent.
                  </li>
                  <li>
                    <strong className="text-foreground">Service availability.</strong>{' '}
                    ClearCommunicationApp is delivered as a copyright-protected
                    digital service. Where applicable, electronically supplied
                    services not falling under Article 4(1)(b) of the Regulation
                    remain accessible to EU/EEA customers in line with our
                    standard terms.
                  </li>
                </ul>
                <p>
                  If you believe you have experienced unjustified geo-blocking
                  while using our service, contact us at{' '}
                  <a
                    href="mailto:clearcommunicationapp@gmail.com"
                    className="text-primary hover:underline"
                  >
                    clearcommunicationapp@gmail.com
                  </a>{' '}
                  and we will respond within a reasonable timeframe.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">12. Contact Us</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>If you have questions about these Terms of Service, contact us:</p>
                    <ul className="space-y-2">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:clearcommunicationapp@gmail.com" className="text-primary hover:underline">clearcommunicationapp@gmail.com</a></li>
                      <li><strong className="text-foreground">Website:</strong> <a href="https://clearcommunicationapp.com" className="text-primary hover:underline">www.clearcommunicationapp.com</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 justify-center pt-8 border-t border-border">
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            <span className="text-muted-foreground">|</span>
            <Link to="/" className="text-primary hover:underline">Return to App</Link>
          </div>
        </div>
        </AutoTranslate>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
